/**
 * تحليل التقرير — عبر خادم Nova، لا عبر متصفح العميل  (الخطة م4.1)
 * ==================================================================
 *
 * ⏳ لماذا الآن: Assistants API يُزال في 26 أغسطس 2026. إزالة لا إهمال — كل نداء
 * لـ /v1/threads و/v1/threads/runs يرجع خطأ بلا فترة سماح. النسخة السابقة من هذا
 * الملف كانت تُجري أربعة نداءات منها، فكل تحليل في الإنتاج كان سيتوقف في ذلك اليوم.
 *
 * ما تغيّر فعلياً: النداء انتقل إلى الخادم. لا أكثر.
 *
 *   قبل:  المتصفح يحمل مفتاح OpenAI الخام ← thread ← message ← run ← polling ← messages
 *   بعد:  POST /api/v1/ai/analyze  ← ردّ واحد
 *
 * المكاسب ليست تجميلية: المفتاح لم يعد يُرسَل للمتصفح أصلاً، وصار هناك حدّ معدّل
 * وحدّ حجم وتخزين نتائج وسلسلة نماذج احتياطية — ولا شيء من ذلك كان ممكناً بينما
 * المتصفح هو من ينادي المزوّد.
 *
 * ⚠️ التوقيع وشكل الردّ مجمّدان حرفياً.
 * تسعة معاملات بالترتيب نفسه، والردّ بالمفاتيح نفسها بما فيها `runTimeline`
 * و`isFallback` الميتان. `analyzeWithAI.js` وكل طبقة العرض لم تُمسّ.
 *
 * ⚠️ الـ prompt ما زال يُشتقّ هنا — عمداً.
 * `prepareDataForChatGPT` دوال نقية تُطبّع المدخلات (reportContext · filters ·
 * normalizedFinancialData · customerContext). الخادم يستقبل هذه القطع الأربع
 * ويعيد تركيب النص نفسه بايتاً ببايت — وهذا مُثبَت باختبار تفاضلي يستورد وحدات
 * المتصفح هذه ويقارنها بمُركِّب الخادم (Testing/Unit/Ai/ai.analyze.test.js).
 * نقل الاشتقاق نفسه إلى الخادم إعادة هيكلة لاحقة، لا شرطاً للخروج من Assistants.
 */
import axios from "axios";
import { BASE_API_URL } from "../../constants";
import { buildAiErrorDetails, isAbortError } from "./client";
import { parseChatGPTResponse } from "./parseChatGPTResponse";
import { logChatGPTPromptDebug, logChatGPTResponseDebug } from "./promptDebug";
import { prepareDataForChatGPT } from "./prepareDataForChatGPT";

/**
 * البثّ مُطفأ عند الإطلاق (ق3 · م4.2).
 *
 * المسار موجود في الطرفين — الخادم يبثّ SSE والمستهلك أدناه يجمّع — لكن سلوك اليوم
 * هو ردّ واحد، والإطلاق بسلوك اليوم حرفياً. تحويله إلى true لا يحتاج تغييراً في
 * الخادم؛ اقرأ التحذير في Modules/Ai/Analyze/Controller.js قبل ذلك.
 */
export const AI_STREAMING_ENABLED = false;

const buildFailureResponse = ({
  error,
  errorMessage,
  prompt,
  rawResponse = "",
  runTimeline = null,
  validation = null,
}) => ({
  success: false,
  isFallback: false,
  errorMessage,
  errorCode: error?.code || "",
  errorStatus: error?.status || "",
  error: error || null,
  data: null,
  metadata: {
    promptLength: prompt.length,
    responseLength: rawResponse.length,
    validation,
    parserValidation: null,
    runTimeline,
  },
});

/**
 * عميل مستقل عن `Services/api` عمداً: ذاك يحمل `AbortController` واحداً على مستوى
 * الوحدة، وإلغاء تحليل واحد كان سيقطع كل نداء آخر في الصفحة. هنا الإشارة تأتي مع
 * كل طلب على حدة.
 *
 * والمهلة أطول من مهلة الخادم (110 ثانية) بهامش، فينتهي الخادم أولاً ويصل خطؤه
 * المُطبَّع بدل مهلة عامة من axios لا تحمل سبباً.
 */
const analyzeClient = axios.create({
  baseURL: BASE_API_URL,
  timeout: 125000,
});

analyzeClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers["X-T"] = `Bearer ${token}`;
  config.headers["Local"] = localStorage.getItem("language") === "English" ? "en" : "ar";
  delete config.headers.Authorization;
  return config;
});

/**
 * خطأ axios ← نفس شكل الخطأ الذي كان `safeFetch` يُنتجه.
 *
 * الأكواد التي يترجمها `client.js` إلى رسائل عربية محصورة في 401 · 403 · 408 · 429
 * · 503، والخادم يُطبّع كل فشل مزوّد إلى إحداها قبل الردّ (م3.1-أ). فما يصل هنا
 * صالح للجدول أصلاً، وهذا يقرؤه لا أكثر.
 */
const extractServerFailure = (error, language) => {
  const payload = error?.response?.data;
  const analysis = payload?.data;
  if (analysis && analysis.success === false) {
    return {
      code: analysis.errorCode || "",
      status: analysis.errorStatus || error?.response?.status || null,
      message: analysis.errorMessage || "",
      metadata: analysis.metadata || null,
    };
  }
  const details = buildAiErrorDetails({
    code: payload?.status,
    language,
    message: payload?.message || error?.message,
    status: error?.response?.status ?? null,
  });
  return { ...details, metadata: null };
};

export const analyzeWithChatGPT = async (
  reportData,
  filters,
  reportType,
  originalApiResponse,
  language,
  excelData = null,
  allowedModels = null,
  user = null,
  executionControl = null
) => {
  let prompt = "";
  let validation = null;
  const runTimeline = null;
  const updateProgress = executionControl?.onProgress;

  try {
    const prepared = prepareDataForChatGPT(
      reportData,
      filters,
      reportType,
      originalApiResponse,
      excelData,
      language,
      allowedModels,
      user
    );

    prompt = prepared.prompt;
    validation = prepared.validation;

    logChatGPTPromptDebug({
      allowedModels,
      enhancedData: prepared.enhancedData,
      excelData,
      filters,
      prompt,
      reportType,
      validation,
    });

    // يُفحص محلياً كما كان: لا معنى لإنفاق طلب على تقرير بلا أرقام. الخادم يفحصه
    // ثانيةً ويرد 422 — الحارسان مقصودان، فالمتصفح ليس مصدراً موثوقاً.
    if (!validation?.hasData) {
      return buildFailureResponse({
        error: { code: "no_financial_data", status: 422, message: "No financial data available for analysis." },
        errorMessage: "No financial data available for analysis.",
        prompt,
        validation,
      });
    }

    const signal = executionControl?.signal;

    // مراحل التقدّم تبقى كما هي رغم أن النداء صار واحداً: شريط التقدّم في الواجهة
    // مبنيّ على هذه المراحل بالاسم، وحذفها يجعله يقفز من 45 إلى 100.
    updateProgress?.({
      progress: 55,
      hasStartedRun: false,
      stage: "creating-thread",
      title: language === "English" ? "Opening AI session" : "جاري فتح جلسة التحليل",
      description:
        language === "English"
          ? "A new analysis session is being created."
          : "يتم إنشاء جلسة جديدة للتحليل.",
    });

    updateProgress?.({
      progress: 65,
      hasStartedRun: true,
      stage: "sending-prompt",
      title: language === "English" ? "Sending analysis data" : "جاري إرسال بيانات التحليل",
      description:
        language === "English"
          ? "The structured request is being uploaded to the AI service."
          : "يتم الآن إرسال الطلب المنظم إلى خدمة الذكاء الاصطناعي.",
    });

    // القطع الأربع المشتقّة، لا نص الـ prompt. الخادم لا يقبل نصاً جاهزاً بحال:
    // لو قَبِله لصار بإمكان أي عميل أن يُملي على النموذج ما يشاء على حساب غيره.
    //
    // `ReportKey` هو `reportType` نفسه — سلسلة مثل Wafeq_vertical_balance_sheet_analysis
    // (انظر reportKeys.js). الخادم يتحقّق من ملكيته من التوكن لا من الطلب.
    const { data: envelope } = await analyzeClient.post(
      "/ai/analyze",
      {
        ReportKey: reportType,
        Filters: prepared.enhancedData.filters || {},
        ReportContext: prepared.enhancedData.reportContext || {},
        FinancialData: prepared.enhancedData.normalizedFinancialData || {},
        CustomerContext: prepared.enhancedData.customerContext || null,
        ExcelData: excelData || null,
        Language: language === "English" ? "English" : "Arabic",
        Stream: AI_STREAMING_ENABLED,
      },
      { signal }
    );

    updateProgress?.({
      progress: 92,
      hasStartedRun: true,
      stage: "fetching-response",
      title: language === "English" ? "Fetching final output" : "جاري جلب المخرجات النهائية",
      description:
        language === "English"
          ? "The final response is being retrieved now."
          : "يتم الآن جلب الاستجابة النهائية.",
    });

    const analysis = envelope?.data || null;

    if (!analysis || analysis.success === false) {
      return buildFailureResponse({
        error: {
          code: analysis?.errorCode || "service_unavailable",
          status: analysis?.errorStatus || 503,
          message: analysis?.errorMessage || "AI analysis is unavailable.",
        },
        errorMessage: analysis?.errorMessage || "AI analysis is unavailable.",
        prompt,
        validation: analysis?.metadata?.validation || validation,
      });
    }

    updateProgress?.({
      progress: 97,
      hasStartedRun: true,
      stage: "parsing-response",
      title: language === "English" ? "Validating JSON contract" : "جاري التحقق من عقد JSON",
      description:
        language === "English"
          ? "The response is being validated against the required report schema."
          : "يتم التحقق من الاستجابة مقابل هيكل التقرير المطلوب.",
    });

    // الخادم يفرض العقد عند المزوّد ثم يتحقّق منه بنيوياً، لكن `parseChatGPTResponse`
    // يبقى شبكة أمان ثانية — وهو أيضاً ما **يُطبّع** التقرير للعرض
    // (parseAiJsonReport). تخطّيه يعني وصول شكل غير مُطبَّع لطبقة العرض لأول مرة.
    // إعادة التسلسل هنا مقصودة: الدالة تستقبل نصاً، وهذا يبقيها على مسارها المعتاد.
    const rawResponse = JSON.stringify(analysis.data?.report ?? null);
    const parsedResponse = parseChatGPTResponse(rawResponse, language);

    logChatGPTResponseDebug({
      parsedResponse,
      rawResponse,
      reportType,
      validation,
      errorMessage: parsedResponse.errorMessage,
    });

    if (!parsedResponse.success) {
      return buildFailureResponse({
        error: {
          code: "invalid_ai_json_contract",
          status: 422,
          message: parsedResponse.errorMessage || "AI response does not match the required JSON contract.",
        },
        errorMessage:
          parsedResponse.errorMessage || "AI response does not match the required JSON contract.",
        prompt,
        rawResponse,
        runTimeline,
        validation: {
          ...validation,
          parserValidation: parsedResponse.validation,
        },
      });
    }

    return {
      success: true,
      isFallback: false,
      errorMessage: "",
      data: {
        success: true,
        report: parsedResponse.report,
      },
      metadata: {
        // مقاسة على الـ prompt المشتقّ محلياً، وهو مطابق لما بناه الخادم بايتاً
        // ببايت — فالرقم يظل صادقاً، وزر تصدير الـ prompt يظل يعمل.
        promptLength: prompt.length,
        responseLength: analysis.metadata?.responseLength ?? rawResponse.length,
        validation: analysis.metadata?.validation || validation,
        parserValidation: parsedResponse.validation,
        runTimeline,
        // إضافيان لا يكسران الشكل المجمّد، ويفيدان في تشخيص الشكاوى.
        servedFromCache: analysis.metadata?.servedFromCache ?? false,
        usedModel: analysis.metadata?.usedModel ?? null,
      },
    };
  } catch (error) {
    // الإلغاء يُرمى كما هو: المستدعي يميّزه عن الفشل، ولا يوجد من يُبلَّغ أصلاً.
    if (isAbortError(error) || error?.code === "ERR_CANCELED") throw error;

    const failure = extractServerFailure(error, language);

    logChatGPTResponseDebug({
      parsedResponse: null,
      rawResponse: "",
      reportType,
      validation,
      errorMessage: failure.message,
    });

    return buildFailureResponse({
      error: {
        code: failure.code,
        status: failure.status,
        message: failure.message,
        type: error?.name,
      },
      errorMessage: failure.message,
      prompt,
      runTimeline,
      validation: failure.metadata?.validation || validation,
    });
  }
};
